import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from './supabase';

// Mirrors a push-flow event to both the JS console and the push_debug_log
// table so that device-side logs are inspectable from the Supabase dashboard
// (useful when Xcode / adb is not available). The DB insert is best-effort —
// never let a log-write failure break the push flow.
async function logPushEvent(event, details) {
  console.log(`[Push] ${event}:`, details);
  try {
    const safeDetails = JSON.parse(JSON.stringify(details ?? null, (_, v) =>
      v instanceof Error ? { name: v.name, message: v.message } : v
    ));
    const { data: { session } } = await supabase.auth.getSession();
    await supabase.from('push_debug_log').insert({
      user_id: session?.user?.id ?? null,
      event,
      details: safeDetails,
    });
  } catch {
    // swallow
  }
}

/**
 * Register for push notifications. Call after user is logged in.
 * Requests permission, gets the device token, sends it to OneSignal
 * via the edge function, and stores the player ID in the profile.
 */
export async function registerForPushNotifications(userId) {
  if (!Capacitor.isNativePlatform()) return;
  if (!userId) return;

  try {
    // Clear any accumulated listeners from prior invocations so a fresh
    // login/relaunch doesn't stack duplicate 'registration' handlers.
    await PushNotifications.removeAllListeners();

    // Check / request permission
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive !== 'granted') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive === 'denied') return;

    // Set up listener for the token BEFORE calling register()
    await PushNotifications.addListener('registration', async (token) => {
      // Route this call through CapacitorHttp (native URLSession on iOS)
      // instead of the WebView fetch, which has been failing with
      // "TypeError: Load failed" on iOS. Scoped to this one request so the
      // rest of the app keeps using standard fetch().
      const FUNCTION_URL = 'https://phsdvraahotvcbrtmunj.supabase.co/functions/v1/register-push-token';
      const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBoc2R2cmFhaG90dmNicnRtdW5qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc1NjU2MjEsImV4cCI6MjA4MzE0MTYyMX0.GzwCOtqVtaTSJVPkih_rqcUA8jsuEZLxe_bzaWSLlN8';

      let data = null;
      let status = null;
      let fetchError = null;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await CapacitorHttp.post({
          url: FUNCTION_URL,
          headers: {
            'Content-Type': 'application/json',
            'apikey': ANON_KEY,
            'Authorization': `Bearer ${session?.access_token ?? ANON_KEY}`,
          },
          data: {
            device_token: token.value,
            device_type: Capacitor.getPlatform() === 'ios' ? 0 : 1,
            external_user_id: userId,
          },
        });
        status = response.status;
        data = response.data;
        if (response.status < 200 || response.status >= 300) {
          fetchError = { stage: 'status', status: response.status, body: response.data };
        }
      } catch (err) {
        fetchError = { stage: 'network', name: err?.name, message: err?.message };
      }

      await logPushEvent('invoke_result', { transport: 'capacitor_http', status, data, error: fetchError });

      if (fetchError) {
        console.error('[Push] Edge function error:', fetchError);
        await logPushEvent('invoke_error', { status, error: fetchError });
        return;
      }

      const playerId = data?.player_id;
      if (!playerId) {
        await logPushEvent('no_player_id', { data });
        return;
      }

      await savePlayerIdToProfile(userId, playerId);
    });

    await PushNotifications.addListener('registrationError', async (error) => {
      console.error('[Push] Registration error:', error);
      await logPushEvent('registration_error', { error });
    });

    // Register with APNs/FCM — triggers 'registration' listener above
    await PushNotifications.register();

  } catch (err) {
    console.error('[Push] Error:', err);
    await logPushEvent('top_level_error', { error: err });
  }
}

// The 'registration' callback can fire asynchronously long after login,
// so we verify an auth session exists (and refresh it if not) before the
// UPDATE, otherwise RLS silently rejects the write with zero affected rows.
async function savePlayerIdToProfile(userId, playerId) {
  await logPushEvent('update_profile', { userId, playerId });

  let { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    console.warn('[Push] No session at update time — attempting refresh');
    await logPushEvent('session_refresh', { userId });
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshed?.session) {
      console.error('[Push] Session refresh failed — aborting profile update', refreshError);
      await logPushEvent('session_refresh_failed', { error: refreshError });
      return;
    }
    session = refreshed.session;
  }

  const { data: updateData, error: updateError } = await supabase
    .from('profiles')
    .update({ onesignal_player_id: playerId })
    .eq('id', userId)
    .select('id, onesignal_player_id');

  if (updateError) {
    console.error('[Push] Failed to save player ID:', updateError);
    await logPushEvent('update_error', { error: updateError });
    return;
  }

  if (!updateData || updateData.length === 0) {
    console.error('[Push] Zero rows updated — likely RLS/auth issue');
    await logPushEvent('zero_rows', { userId, playerId });
    return;
  }

  await logPushEvent('update_success', { row: updateData[0] });
}

/**
 * No-op on logout — kept for API compatibility.
 */
export function removeExternalUserId() {}
